'use client';

import React from 'react';
import { ContactFormFields } from './ContactFormFields';
import { FormSubmissionResult } from './FormSubmissionResult';
import { useContactForm } from '../lib/useContactForm';
import { useHubSpotFormFields } from '../lib/useHubSpotFormFields';
import { Typography } from '@workspace/ui/common';

export interface ContactFormProps {
  /** Optional heading displayed above the form. */
  heading?: string;
  /**
   * HubSpot form GUID (e.g. "294f5d51-d992-4c6f-a42b-cc82eea4bb1b").
   * When provided, field definitions are fetched from the HubSpot Forms V2 API
   * and the form submits to that specific HubSpot form.
   * When omitted, the default contact form fields and GUID from env are used.
   */
  formId?: string;
}

export function ContactForm({ heading, formId }: ContactFormProps) {
  // Step 1: Resolve field definitions.
  // - formId set  → fetch from /api/hubspot/form-fields (HubSpot Forms V2 API).
  // - no formId   → returns contactFormFieldsConfig immediately (no fetch needed).
  const { fields, loading: fieldsLoading, error } = useHubSpotFormFields(formId);

  // Step 2: Initialise the form. In dynamic mode (fields provided), useContactForm
  // builds submission data from the field list so any HubSpot field name works.
  const {
    form,
    loading: submitting,
    result,
    showForm,
    handleMessageChange,
    handleInvalidSubmit,
    onSubmit,
  } = useContactForm({ formId, fields: fields.length > 0 ? fields : undefined });

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="font-openSans">
        {heading && showForm && (
          <Typography variant="h3" className="pb-md md:pb-2xl" styleVariant="h3">
            {heading}
          </Typography>
        )}
        <Typography variant="bodyMd" className="text-error">
          {error}
        </Typography>
      </div>
    );
  }

  // ── Loading skeleton while field definitions are being fetched ──────────
  if (fieldsLoading) {
    return (
      <div className="font-openSans animate-pulse space-y-xl">
        {heading && <div className="h-8 w-64 rounded bg-secondary-100" />}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-lg gap-x-md">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-xs">
              <div className="h-4 w-24 rounded bg-secondary-100" />
              <div className="h-11 w-full rounded bg-secondary-100" />
            </div>
          ))}
        </div>
        <div className="h-11 w-full md:w-60 rounded bg-secondary-100" />
      </div>
    );
  }

  return (
    <div className="font-openSans">
      {heading && showForm && (
        <Typography variant="h3" className="pb-md md:pb-2xl" styleVariant="h3">
          {heading}
        </Typography>
      )}

      {!result ? (
        <ContactFormFields
          form={form}
          loading={submitting}
          onMessageChange={handleMessageChange}
          onSubmit={onSubmit}
          onInvalidSubmit={handleInvalidSubmit}
          showForm={showForm}
          fields={fields.length > 0 ? fields : undefined}
        />
      ) : (
        <FormSubmissionResult result={result} />
      )}
    </div>
  );
}
